import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Camera } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { usePatientToothImages } from '@/hooks/useToothImages';
import { ImageUploader } from './ImageUploader';
import { ImageGallery } from './ImageGallery';
import { ImagePreviewSidebar } from './ImagePreviewSidebar';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ImagingPanelProps {
  patientId: string;
  clinicId: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ImagingPanel({ patientId, clinicId }: ImagingPanelProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: images, isLoading, error } = usePatientToothImages(patientId);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  const selectedImage = images?.find((img) => img.id === selectedImageId) ?? null;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-red-400">
        {t('common.error')}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <Camera className="h-5 w-5 text-indigo-500" />
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
          {t('dental.imaging.title')}
        </h2>
        {images && images.length > 0 && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {images.length}
          </span>
        )}
      </div>

      {/* ── Uploader ────────────────────────────────────────────────────────── */}
      <ImageUploader clinicId={clinicId} patientId={patientId} />

      {/* ── Gallery + sidebar ──────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 gap-4">
        <div className="flex flex-1 flex-col min-w-0">
          <ImageGallery
            images={images ?? []}
            patientId={patientId}
            userId={user?.id ?? ''}
            filterToothFdi={null}
          />
        </div>

        {selectedImage && (
          <ImagePreviewSidebar
            image={selectedImage}
            patientId={patientId}
            onClose={() => setSelectedImageId(null)}
          />
        )}
      </div>
    </div>
  );
}
